import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const GroupStageKnockoutContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The group stage + knockout format is the structure behind the FIFA World Cup, the
      UEFA Champions League, and most major esports events. It splits your tournament into
      two phases: a round robin group stage where everyone gets guaranteed matches, followed
      by a single or double elimination knockout bracket for the qualifiers. The result is a
      format that is both fair and dramatic — players get real playing time before the
      pressure of win-or-go-home rounds. This guide explains exactly how it works and how to
      run one for your own event.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How the group stage + knockout format works
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      The field is divided into several small groups of 3–6 players or teams. Within each
      group, every participant plays every other participant once — a mini round robin. After
      all group matches finish, the top finishers from each group (usually the top 1 or 2)
      advance to a knockout bracket. From there it is standard elimination: lose and you are
      out, until one champion remains. The group stage rewards consistency; the knockout stage
      rewards clutch performance.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Why organizers choose it
    </Text>
    {[
      'Guaranteed matches — nobody travels to your event to play a single game and go home',
      'Fairer than pure single elimination — one bad match in the group stage does not end your run',
      'Scales to large fields — 32 players split into 8 groups stay manageable',
      'Builds narrative — group standings create stakes before the knockout drama begins',
      'Seeds the bracket naturally — group winners are rewarded with easier knockout draws',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Choosing your group sizes
    </Text>
    {[
      {
        label: '16 players → 4 groups of 4',
        body: 'Top 2 from each group advance to an 8-player knockout bracket. The classic World Cup structure. 24 group matches + 7 knockout matches.',
      },
      {
        label: '24 players → 6 groups of 4',
        body: 'Top 2 advance to a 12-team knockout (4 byes for the best group winners), or top 1 + best runners-up fill a clean 8-team bracket.',
      },
      {
        label: '32 players → 8 groups of 4',
        body: 'Top 2 advance to a 16-player knockout bracket. The cleanest large-field structure — no byes, no awkward math.',
      },
      {
        label: 'Small field (8–12)',
        body: '2 groups feeding a 4-team semifinal works, but with fewer than 12 players a single round robin or straight elimination is often simpler.',
      },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 14, lineHeight: 22, color: colors.textSecondary }}>{item.body}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Group stage tie-breakers
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      When two players finish the group with the same record, you need a clear tie-break order
      decided <Text style={{ fontWeight: '700' }}>before</Text> the event starts. The standard
      hierarchy, in order, is:
    </Text>
    {[
      'Head-to-head result between the tied players',
      'Point or goal difference (points scored minus points conceded)',
      'Total points or goals scored',
      'A playoff match or coin flip as a final fallback',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Seeding the knockout bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      To keep the bracket fair, group winners should never meet a runner-up from their own group
      in the first knockout round. The standard cross-pairing is winner of Group A versus
      runner-up of Group B, winner of Group B versus runner-up of Group A, and so on. This
      rewards finishing first in your group and avoids two players from the same group meeting
      again immediately. If you are unsure how to order your bracket, our{' '}
      <Text style={{ fontWeight: '700' }}>seeding guide</Text> covers the full method.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Common mistakes to avoid
    </Text>
    {[
      'Groups that are too large — 6+ players per group makes the group stage drag on for hours',
      'No published tie-break rules — disputes erupt the moment two players finish level',
      'Letting same-group players meet in round 1 of the knockout — it defeats the purpose of groups',
      'Advancing too many players — if most of the field qualifies, the group stage means little',
      'Forgetting to account for byes when the qualifier count is not a power of two',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Running it in Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly makes the two-phase structure easy. Create a round robin tournament for each
      group — Tournaly schedules every pairing automatically, tracks wins and losses, and
      computes standings with built-in tie-break rules. Once the groups finish, create a single
      or double elimination tournament for the qualifiers, and Tournaly generates the seeded
      knockout bracket, routes winners automatically, and detects the champion. Group leagues
      together with Tournaly Leagues to keep all the standings in one place, share a live bracket
      link, and export the final results. It is the fastest way to run a World Cup-style event
      without spreadsheets.
    </Text>
  </View>
);

const GroupStageKnockoutContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      El formato de fase de grupos + eliminación es la estructura del Mundial de la FIFA, la
      Champions League de la UEFA y la mayoría de los grandes eventos de esports. Divide tu
      torneo en dos fases: una fase de grupos round robin donde todos tienen partidos
      garantizados, seguida de un bracket de eliminación simple o doble para los clasificados.
      El resultado es un formato a la vez justo y dramático — los jugadores tienen tiempo de
      juego real antes de la presión de las rondas a vida o muerte. Esta guía explica
      exactamente cómo funciona y cómo organizar uno para tu propio evento.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Cómo funciona el formato fase de grupos + eliminación
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      El campo se divide en varios grupos pequeños de 3–6 jugadores o equipos. Dentro de cada
      grupo, cada participante juega contra todos los demás una vez — un mini round robin.
      Cuando terminan todos los partidos de grupo, los mejores de cada grupo (normalmente el
      1.º o los 2 primeros) avanzan a un bracket de eliminación. A partir de ahí es eliminación
      estándar: pierdes y quedas fuera, hasta que queda un campeón. La fase de grupos premia la
      consistencia; la fase eliminatoria premia el rendimiento bajo presión.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Por qué lo eligen los organizadores
    </Text>
    {[
      'Partidos garantizados — nadie viaja a tu evento para jugar un solo partido e irse a casa',
      'Más justo que la eliminación simple pura — un mal partido en la fase de grupos no acaba tu torneo',
      'Escala a campos grandes — 32 jugadores divididos en 8 grupos siguen siendo manejables',
      'Crea narrativa — la clasificación de grupos genera tensión antes del drama eliminatorio',
      'Siembra el bracket de forma natural — los ganadores de grupo se premian con cruces más fáciles',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Elegir el tamaño de los grupos
    </Text>
    {[
      {
        label: '16 jugadores → 4 grupos de 4',
        body: 'Los 2 primeros de cada grupo avanzan a un bracket de 8. La estructura clásica del Mundial. 24 partidos de grupo + 7 eliminatorios.',
      },
      {
        label: '24 jugadores → 6 grupos de 4',
        body: 'Los 2 primeros avanzan a una eliminatoria de 12 (4 byes para los mejores ganadores de grupo), o el 1.º + los mejores segundos llenan un bracket limpio de 8.',
      },
      {
        label: '32 jugadores → 8 grupos de 4',
        body: 'Los 2 primeros avanzan a un bracket de 16. La estructura más limpia para campos grandes — sin byes, sin cálculos incómodos.',
      },
      {
        label: 'Campo pequeño (8–12)',
        body: '2 grupos alimentando una semifinal de 4 funciona, pero con menos de 12 jugadores un round robin único o eliminación directa suele ser más simple.',
      },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 14, lineHeight: 22, color: colors.textSecondary }}>{item.body}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Desempates en la fase de grupos
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Cuando dos jugadores terminan el grupo con el mismo récord, necesitas un orden de
      desempate claro decidido <Text style={{ fontWeight: '700' }}>antes</Text> de empezar el
      evento. La jerarquía estándar, en orden, es:
    </Text>
    {[
      'Resultado directo entre los jugadores empatados',
      'Diferencia de puntos o goles (anotados menos recibidos)',
      'Total de puntos o goles anotados',
      'Un partido de desempate o sorteo como último recurso',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Sembrar el bracket eliminatorio
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Para que el bracket sea justo, los ganadores de grupo nunca deben enfrentarse a un segundo
      de su propio grupo en la primera ronda eliminatoria. El cruce estándar es ganador del
      Grupo A contra segundo del Grupo B, ganador del Grupo B contra segundo del Grupo A, y así
      sucesivamente. Esto premia terminar primero en tu grupo y evita que dos jugadores del
      mismo grupo se reencuentren de inmediato. Si no estás seguro de cómo ordenar tu bracket,
      nuestra <Text style={{ fontWeight: '700' }}>guía de siembra</Text> cubre el método completo.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Errores comunes a evitar
    </Text>
    {[
      'Grupos demasiado grandes — 6+ jugadores por grupo alarga la fase de grupos durante horas',
      'No publicar las reglas de desempate — surgen disputas en cuanto dos jugadores empatan',
      'Dejar que jugadores del mismo grupo se crucen en la ronda 1 eliminatoria — anula el sentido de los grupos',
      'Clasificar a demasiados jugadores — si casi todo el campo avanza, la fase de grupos vale poco',
      'Olvidar contar los byes cuando el número de clasificados no es potencia de dos',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Organizarlo en Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly facilita la estructura de dos fases. Crea un torneo round robin para cada grupo —
      Tournaly programa cada emparejamiento automáticamente, registra victorias y derrotas y
      calcula la clasificación con reglas de desempate integradas. Cuando los grupos terminan,
      crea un torneo de eliminación simple o doble para los clasificados, y Tournaly genera el
      bracket eliminatorio con cabezas de serie, enruta a los ganadores automáticamente y detecta
      al campeón. Agrupa las ligas con Tournaly Leagues para mantener todas las clasificaciones en
      un solo lugar, comparte un enlace de bracket en directo y exporta los resultados finales. Es
      la forma más rápida de organizar un evento estilo Mundial sin hojas de cálculo.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-06-29-group-stage-knockout-tournament-guide',
  title: 'Group Stage + Knockout Tournament Format: The Complete Guide',
  titleEs: 'Formato de Torneo Fase de Grupos + Eliminación: La Guía Completa',
  date: '2026-06-29',
  summary: 'A complete guide to the group stage + knockout tournament format used by the World Cup and Champions League — how it works, choosing group sizes, tie-breakers, seeding the bracket, and running one in Tournaly.',
  summaryEs: 'Una guía completa del formato de torneo fase de grupos + eliminación usado en el Mundial y la Champions League — cómo funciona, elegir el tamaño de los grupos, desempates, sembrar el bracket y organizarlo en Tournaly.',
  tags: ['Guide', 'Formats', 'Group Stage', 'Knockout'],
  Content: GroupStageKnockoutContent,
  ContentEs: GroupStageKnockoutContentEs,
};
